import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Users, Briefcase, FolderKanban, Receipt, Calendar, 
  TrendingUp, DollarSign, Clock, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, subValue, trend, color }) => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
        color === 'blue' ? 'bg-[#014DB7]/10' :
        color === 'green' ? 'bg-[#84CC16]/10' :
        color === 'purple' ? 'bg-purple-100' :
        'bg-orange-100'
      }`}>
        <Icon className={`w-6 h-6 ${
          color === 'blue' ? 'text-[#014DB7]' :
          color === 'green' ? 'text-[#84CC16]' :
          color === 'purple' ? 'text-purple-600' :
          'text-orange-600'
        }`} />
      </div>
      {trend && (
        <span className={`flex items-center gap-1 text-sm font-medium ${
          trend > 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          {trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <div className="mt-4">
      <h3 className="text-3xl font-bold text-[#0A1628]">{value}</h3>
      <p className="text-gray-500 text-sm mt-1">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
  </div>
);

const RecentActivity = ({ activities }) => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
    <h3 className="font-semibold text-[#0A1628] mb-4">Recent Activity</h3>
    <div className="space-y-4">
      {activities.length === 0 ? (
        <p className="text-gray-500 text-sm">No recent activity</p>
      ) : (
        activities.map((activity, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <div className="w-2 h-2 mt-2 rounded-full bg-[#014DB7]" />
            <div>
              <p className="text-sm text-gray-700">{activity.details || activity.action}</p>
              <p className="text-xs text-gray-400">{new Date(activity.created_at).toLocaleString()}</p>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const LeadsByStatus = ({ leads }) => {
  const statuses = ['new', 'contacted', 'qualified', 'proposal', 'won', 'lost'];
  const statusCounts = statuses.map(status => ({
    status,
    count: leads.filter(l => l.status === status).length
  }));
  const maxCount = Math.max(...statusCounts.map(s => s.count), 1);

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
      <h3 className="font-semibold text-[#0A1628] mb-4">Leads Pipeline</h3>
      <div className="space-y-3">
        {statusCounts.map(({ status, count }) => (
          <div key={status}>
            <div className="flex justify-between text-sm mb-1">
              <span className="capitalize text-gray-600">{status}</span>
              <span className="font-medium text-[#0A1628]">{count}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#014DB7] to-[#84CC16] rounded-full transition-all duration-500"
                style={{ width: `${(count / maxCount) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DashboardPage = () => {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, leadsRes, activitiesRes] = await Promise.all([
          api.get('/api/dashboard/stats'),
          api.get('/api/leads'),
          api.get('/api/activity?limit=10'),
        ]);
        setStats(statsRes.data);
        setLeads(leadsRes.data);
        setActivities(activitiesRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Dashboard</h1>
        <p className="text-gray-500">Welcome to your CRM overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          icon={Users} 
          label="Total Leads" 
          value={stats?.leads?.total || 0}
          subValue={`${stats?.leads?.new || 0} new leads`}
          color="blue"
        />
        <StatCard 
          icon={Briefcase} 
          label="Active Projects" 
          value={stats?.projects?.active || 0}
          subValue={`${stats?.projects?.total || 0} total projects`}
          color="green"
        />
        <StatCard 
          icon={FolderKanban} 
          label="Pending Tasks" 
          value={stats?.tasks?.pending || 0}
          subValue={`${stats?.tasks?.total || 0} total tasks`}
          color="purple"
        />
        <StatCard 
          icon={DollarSign} 
          label="Revenue" 
          value={`$${(stats?.revenue || 0).toLocaleString()}`}
          subValue={`${stats?.invoices?.pending || 0} pending invoices`}
          color="orange"
        />
      </div>

      {/* Charts & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <LeadsByStatus leads={leads} />
        </div>
        <RecentActivity activities={activities} />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-[#014DB7] to-[#0066FF] rounded-2xl p-6 text-white">
          <Calendar className="w-8 h-8 mb-3 opacity-80" />
          <h3 className="text-3xl font-bold">{stats?.bookings_today || 0}</h3>
          <p className="text-white/80">Bookings Today</p>
        </div>
        <div className="bg-gradient-to-br from-[#84CC16] to-[#65a30d] rounded-2xl p-6 text-white">
          <Users className="w-8 h-8 mb-3 opacity-80" />
          <h3 className="text-3xl font-bold">{stats?.users || 0}</h3>
          <p className="text-white/80">Team Members</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-6 text-white">
          <Receipt className="w-8 h-8 mb-3 opacity-80" />
          <h3 className="text-3xl font-bold">{stats?.invoices?.total || 0}</h3>
          <p className="text-white/80">Total Invoices</p>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
